/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { format, parseISO } from 'date-fns';

import SociollaText from '@components/sociolla/SociollaText';
import SociollaTable from '@components/sociolla/SociollaTable';
import Image from '@components/global/Image';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import tableApi from '@/hooks/tableApi';
import {
  getPriceRulesProductLog,
  getPriceRulesProductLogCount,
} from '@api/ms-catalog';
import { generateStatus, escapeRegexChars } from '@global/helpers';

const HEADER_DEKSTOP = [
  {
    label: 'Date',
    data: 'date',
    width: '15%',
    numberOfLines: 3,
  },
  {
    label: 'Image',
    data: 'image',
    type: 'image',
    width: '10%',
    styleItem: {
      alignItems: 'center',
      padding: 0,
      margin: 0,
    },
  },
  {
    label: 'Product Name',
    data: 'name',
    width: '20%',
    numberOfLines: 4,
  },
  {
    label: 'From',
    data: 'from',
    type: 'price',
    width: '10%',
  },
  {
    label: 'To',
    data: 'to',
    type: 'price',
    width: '15%',
  },
  {
    label: 'User',
    data: 'user',
    width: '15%',
    numberOfLines: 3,
  },
  {
    label: 'Status',
    data: 'status',
    width: '15%',
  },
];

const defaultPhoto = require('@/assets/img/default/user-default.png');

function PricelistLogs(props) {
  const tableRef = useRef();

  const productLogs = tableApi(
    getPriceRulesProductLogCount,
    getPriceRulesProductLog,
  );

  const [loadingMore, setLoadingMore] = useState(false);
  const [list, setList] = useState([]);
  const [paramsTable, setParamsTable] = useState({
    filter: {
      target_type: 'product-price-rule',
      target_id: props.id,
    },
    limit: isWeb ? 10 : 15,
    skip: 0,
  });

  const isFiltered = useMemo(
    () => Boolean(Object.keys(paramsTable.filter).length > 2),
    [paramsTable.filter],
  );

  const renderItem = ({ item, index }) => (
    <View key={'list-product-' + index} style={styles.tableRow}>
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <Image
          source={{ uri: `${item.image ? item.image : defaultPhoto}` }}
          style={{ width: 36, height: 36 }}
          width={36}
          height={36}
          defaultSource={defaultPhoto}
        />
        <View style={{ marginLeft: 12, flex: 1 }}>
          <SociollaText
            type="medium"
            style={{
              fontSize: 14,
              color: Colors.black1,
              paddingBottom: 8,
              paddingRight: 125,
            }}
            numberOfLines={3}>
            {item.name}
          </SociollaText>
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          width: '100%',
          alignItems: 'center',
          marginTop: 13,
        }}>
        <View style={{ flex: 1.5 }}>
          <SociollaText style={{ fontSize: 10, color: Colors.black5 }}>
            User:
          </SociollaText>
          <SociollaText
            style={{ fontSize: 12, color: Colors.black5, marginTop: 7 }}>
            {item.user}
          </SociollaText>
        </View>
        <View style={{ flex: 1 }}>
          <SociollaText style={{ fontSize: 10, color: Colors.black5 }}>
            From:
          </SociollaText>
          <SociollaText
            style={{ fontSize: 12, color: Colors.black, marginTop: 7 }}
            currency={true}>
            {item.from}
          </SociollaText>
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          width: '100%',
          alignItems: 'center',
          marginTop: 12,
        }}>
        <View style={{ flex: 1.5 }}>
          <SociollaText style={{ fontSize: 10, color: Colors.black5 }}>
            Date:
          </SociollaText>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: 7,
            }}>
            <Image
              style={{ width: 16, height: 16, marginRight: 8 }}
              width={16}
              height={16}
              svg={true}
              source={require('@assets/img/icons/menu/calendar.svg')}
            />
            <SociollaText style={{ fontSize: 12, color: Colors.black5 }}>
              {item.date}
            </SociollaText>
          </View>
        </View>
        <View style={{ flex: 1 }}>
          <SociollaText style={{ fontSize: 10, color: Colors.black5 }}>
            From:
          </SociollaText>
          <SociollaText
            style={{ fontSize: 12, color: Colors.black, marginTop: 7 }}
            currency={true}>
            {item.to}
          </SociollaText>
        </View>
      </View>
      <SociollaText
        style={[
          item.statusStyle,
          {
            position: 'absolute',
            top: 10,
            right: 12,
            fontSize: 10,
            color: Colors.black5,
          },
        ]}>
        {item.status}
      </SociollaText>
    </View>
  );

  const generateData = (data) => {
    const logs = [];
    for (let index = 0; index < data.length; index++) {
      const original = data[index].original;
      const changed = data[index].changed;
      const dt = data[index];
      if (original && changed) {
        const log = { ...data[index] };
        log.date = dt.created_at
          ? format(parseISO(dt.created_at), 'dd MMM yyyy  HH:mm:ss')
          : '';
        log.user = dt.updated_by?.name || '';
        log.from = original.price;
        log.to = dt.action_type === 'delete' ? original.price : changed.price;
        if (dt.status) {
          const status = generateStatus('price_rules', dt.action_type, 'value');
          log.status = status.label;
          log.statusStyle = {
            fontSize: 10,
            color: Colors.black,
            backgroundColor: status.color,
            paddingHorizontal: 15,
            paddingVertical: 7,
            borderRadius: 4,
            width: 120,
            textAlign: 'center',
            marginLeft: isWeb ? -30 : 0,
          };
        }
        logs.push(log);
      }
    }
    return logs;
  };

  const onSearchTable = async (param, type) => {
    const params = { ...paramsTable };
    if (type === 'pagination') {
      setLoadingMore(true);
      params.limit = param.limit;
      params.skip = param.skip;
    } else if (type === 'keyword') {
      if (param.keyword.length > 2) {
        params.filter['$or'] = [
          {
            name: { $regex: escapeRegexChars(param.keyword), $options: 'i' },
          },
          {
            'original.price': { $regex: param.keyword, $options: 'i' },
          },
          {
            'changed.price': { $regex: param.keyword, $options: 'i' },
          },
        ];
      } else if (!param.keyword) {
        delete params.filter['$or'];
      }
    }
    await setParamsTable({ ...params });
    apiLogs(params);
  };

  const apiLogs = (params = null) => {
    const param = params ? params : { ...paramsTable };
    param.sort = '-created_at';
    productLogs.request(param).then((response) => {
      if (response) {
        setList(generateData(response));
        setLoadingMore(false);
      }
    });
  };

  useEffect(() => {
    if (props.id) {
      apiLogs();
    }
  }, [props.id]);

  return (
    <View style={props.style}>
      {isWeb && (
        <SociollaTable
          table={HEADER_DEKSTOP}
          ref={tableRef}
          datas={list}
          showSearch={true}
          searchBorder={true}
          scrollItem={true}
          showPagination={true}
          stickyHeaderIndices={[0]}
          searchPlaceholder="Search by Name, SKU or Pricelist"
          emptyLabel={'No logs found.'}
          count={productLogs.count}
          loading={productLogs.loading}
          loadingLoadMore={loadingMore}
          onSearchTable={(val, type) => onSearchTable(val, type)}
          localParams={paramsTable}
          isFiltered={isFiltered}
        />
      )}
      {!isWeb && (
        <SociollaTable
          datas={list}
          itemComponent={renderItem}
          headerComponent={<></>}
          emptyLabel={'No logs found.'}
          showSearch={true}
          loading={productLogs.loading}
          count={productLogs.count}
          scrollItem={true}
          hideSearch={true}
          loadingLoadMore={loadingMore}
          searchPlaceholder="Search by Name, SKU or Pricelist"
          onSearchTable={(val, type) => onSearchTable(val, type)}
          localParams={paramsTable}
          isFiltered={isFiltered}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    height: 152,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingHorizontal: 15,
    paddingVertical: 7,
  },
});

export default PricelistLogs;
